/**
 * AsterDex WebSocket adapter — Binance-compatible public stream.
 *
 * wss://fstream.asterdex.com/stream?streams=<symbol>@markPrice@1s
 */

import type { ExchangeWsAdapter } from "../live-pricing.types";

const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];

function normalizeSymbol(symbol: string): string {
  const upper = symbol.toUpperCase();
  return upper.endsWith("USDT") ? upper : `${upper}USDT`;
}

interface AsterDexMarkPricePayload {
  e: string;
  s: string;
  p: string;
  i: string;
  r: string;
  T: number;
  E: number;
  P?: string;
}

interface AsterDexWsMsg {
  stream?: string;
  data?: AsterDexMarkPricePayload;
}

export const asterdexWsAdapter: ExchangeWsAdapter = {
  subscribe(symbol, onUpdate, onError) {
    const normalized = normalizeSymbol(symbol);
    const stream = `${normalized.toLowerCase()}@markPrice@1s`;

    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;

    function connect() {
      if (stopped) return;
      ws = new WebSocket(
        `wss://fstream.asterdex.com/stream?streams=${stream}`
      );

      ws.onmessage = (e: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(e.data) as AsterDexWsMsg;
          const payload = msg.data;
          if (!payload || payload.e !== "markPriceUpdate") return;

          const markPrice = Number.parseFloat(payload.p);
          if (markPrice <= 0) return;

          attempt = 0;
          onUpdate({
            symbol: normalized,
            exchange: "asterdex",
            markPrice,
            indexPrice: Number.parseFloat(payload.i),
            fundingRate: Number.parseFloat(payload.r),
            nextFundingTime: payload.T,
            timestamp: payload.E,
          });
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        // followed by close
      };

      ws.onclose = () => {
        if (stopped) return;
        const delay =
          RECONNECT_DELAYS_MS[
            Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
          ];
        attempt++;
        if (attempt > RECONNECT_DELAYS_MS.length) {
          onError?.(new Error(`AsterDex WS closed after ${attempt} attempts`));
          return;
        }
        setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      stopped = true;
      ws?.close();
      ws = null;
    };
  },
};
